import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactDOM from 'react-dom';
import SparkMD5 from 'spark-md5';
import Constants from '../../submodules/logictry_config/constants';
import ConfirmDialog from '../ConfirmDialog/index';
import CreateProject from '../../services/CreateProject';
import Server from '../../services/server';
import Popup from '../Popup/index';
import CopyHtmlToClipboardButton from '../CopyHtmlToClipboardButton';
import { CloseIcon, DeleteIcon, HtmlIcon, ImagesIcon, UploadIcon } from '../../styledhtml/Icon';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import NativeCamera from '../../services/NativeCamera';
import convertUrlfromS3toCloudFront from '../../utils/convertUrlfromS3toCloudFront';
import checkContentType, { logictryUploadedVideoToCdnPlaybackUrl } from '../../utils/checkContentType';
import UserAccount, { LOGGED_OUT_USER_ID } from '../../services/UserAccount';
import { ModalSheetTitle, SmallHint } from '../../styledhtml/Tailwind/index';
/** Match Logictry-supported video uploads (recording may still emit .webm; filename hint only applies to mov/mp4/m4v). */
const TRANSCODER_VIDEO_EXT_RE = /\.(mov|mp4|m4v)$/i;
/** Asset cards skip the HLS player for this long after createdTime. MediaConvert has not written the .m3u8 yet. */
const VIDEO_TRANSCODE_PROCESSING_MS = 20 * 60 * 1000;

function uploadedVideoNeedsTranscode(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    return TRANSCODER_VIDEO_EXT_RE.test(new URL(url).pathname);
  } catch {
    return TRANSCODER_VIDEO_EXT_RE.test(url.split('?')[0]);
  }
}

function videoTranscodeProcessingRemaining(createdTime) {
  if (createdTime == null || createdTime === '') return 0;
  const t = new Date(createdTime).getTime();
  if (!Number.isFinite(t)) return 0;
  const elapsed = Date.now() - t;
  if (elapsed < 0) return VIDEO_TRANSCODE_PROCESSING_MS;
  const remaining = VIDEO_TRANSCODE_PROCESSING_MS - elapsed;
  return remaining > 0 ? remaining : 0;
}

function VideoProcessingLabel() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        width: '100%',
        height: '100%',
        padding: '12px 16px',
        background: '#000',
        color: '#fff',
        fontSize: 13,
        lineHeight: 1.45,
        textAlign: 'center',
      }}
    >
      This video is being processed. It will be ready to play shortly.
    </div>
  );
}

const PORTRAIT_CAPTURE_ASPECT = 9 / 16;
const PORTRAIT_RECORD_WIDTH = 1080;
const PORTRAIT_RECORD_HEIGHT = 1920;
const MAX_FILE_UPLOAD_BYTES = 25000000;
const MAX_VIDEO_UPLOAD_BYTES = 50000000;
/** Story in-app recording (see `forcePortraitVideoCapture`). */
const STORY_MAX_RECORDING_MS = 60000;

/**
 * Viewport-at-upload hint for naming only (MediaConvert chooses template from `*.portrait.*` before ext).
 * @returns {'portrait' | 'landscape'}
 */
function captureViewportVideoOrientationHint() {
  if (typeof window === 'undefined') return 'landscape';
  try {
    if (typeof window.matchMedia === 'function' && window.matchMedia('(orientation: portrait)').matches) {
      return 'portrait';
    }
  } catch {
    //
  }
  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
}

/**
 * `video_transcoder` reads orientation from the object key segment `*.portrait.(mov|mp4|m4v)`.
 * Landscape omits the token (legacy); `.landscape.` is stripped if viewport is portrait re-upload fixes.
 *
 * @param {string} originalName
 * @param {{ forcePortrait?: boolean }} [opts] Story capture sets `forcePortrait` on in-app recordings only.
 */
function videoUploadFilenameForTranscoder(originalName, opts = {}) {
  if (typeof originalName !== 'string' || !TRANSCODER_VIDEO_EXT_RE.test(originalName)) {
    return originalName;
  }
  const extM = originalName.match(/(\.(mov|mp4|m4v))$/i);
  if (!extM) return originalName;
  const ext = extM[1];
  let stem = originalName.slice(0, -ext.length);
  stem = stem.replace(/\.(portrait|landscape)$/i, '');
  const tagPortrait = opts.forcePortrait || captureViewportVideoOrientationHint() === 'portrait';
  if (tagPortrait) {
    return `${stem}.portrait${ext}`;
  }
  return `${stem}${ext}`;
}

/** Front-facing camera: mirror preview and capture so left/right match what users expect. */
function isSelfieFacingMode(facingMode) {
  return facingMode === 'user';
}

/** Center-crop source video to 9:16 for story-style portrait capture. */
function portraitCropSourceRect(videoWidth, videoHeight, targetAspect = PORTRAIT_CAPTURE_ASPECT) {
  const sourceAspect = videoWidth / videoHeight;
  if (sourceAspect > targetAspect) {
    const cropW = videoHeight * targetAspect;
    return { sx: (videoWidth - cropW) / 2, sy: 0, sw: cropW, sh: videoHeight };
  }
  const cropH = videoWidth / targetAspect;
  return { sx: 0, sy: (videoHeight - cropH) / 2, sw: videoWidth, sh: cropH };
}
/** Desktop browsers mostly ignore `<input capture>`, so prefer getUserMedia for still capture there. */
function prefersCaptureAttributeForPhoto() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent || '')) return true;
  if (/Android/i.test(navigator.userAgent || '')) return true;
  if ((navigator.maxTouchPoints || 0) > 0 && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches) return true;
  return false;
}

/**
 * Stories on a phone held upright: use the native camera stream and framing.
 * Desktop story capture still center-crops a landscape webcam to 9:16 via canvas + CSS frame.
 */
function isNativePortraitStoryCapture(forcePortrait) {
  if (!forcePortrait) return false;
  if (typeof window === 'undefined') return false;
  const isPhoneLike = Constants.isIOS
    || Constants.isAndroid
    || prefersCaptureAttributeForPhoto();
  return isPhoneLike && captureViewportVideoOrientationHint() === 'portrait';
}

/**
 * Prefer 1080p then 720p @ ~30fps; honor facingMode when device supports it (mobile).
 * Falls back through simpler constraints so desktop / odd devices still open a camera.
 *
 * Avoid swapping this for «native only» constraints on phones: portrait recordings kept correct
 * vertical framing with these defaults; looser constraints can change negotiated geometry.
 *
 * Playback rotation / mixed aspect after upload is handled by **transcode** (AWS MediaConvert)
 * respecting source rotation metadata—not by overriding these ideals here.
 * @param {boolean} audioEnabled
 * @param {'user' | 'environment'} [facingMode='user'] — front vs back on phones
 * @param {{ forcePortrait?: boolean }} [opts] Stories: prefer 9:16 camera + crop on desktop.
 */
function requestCameraMedia(audioEnabled, facingMode = 'user', opts = {}) {
  if (!navigator.mediaDevices?.getUserMedia) {
    return Promise.reject(new Error('getUserMedia unavailable'));
  }
  const { forcePortrait = false } = opts;
  if (isNativePortraitStoryCapture(forcePortrait)) {
    const mobileAttempts = [
      { video: { facingMode }, audio: audioEnabled },
      { video: true, audio: audioEnabled },
    ];
    let mobileChain = Promise.reject(new Error('retry'));
    mobileAttempts.forEach((constraints) => {
      mobileChain = mobileChain.catch(() => navigator.mediaDevices.getUserMedia(constraints));
    });
    return mobileChain;
  }
  const landscapeAttempts = [
    {
      video: {
        facingMode,
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 30 },
      },
      audio: audioEnabled,
    },
    {
      video: {
        facingMode,
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 },
      },
      audio: audioEnabled,
    },
  ];
  const portraitAttempts = forcePortrait
    ? [
      {
        video: {
          facingMode,
          width: { ideal: PORTRAIT_RECORD_WIDTH },
          height: { ideal: PORTRAIT_RECORD_HEIGHT },
          aspectRatio: { ideal: PORTRAIT_CAPTURE_ASPECT },
          frameRate: { ideal: 30 },
        },
        audio: audioEnabled,
      },
      {
        video: {
          facingMode,
          width: { ideal: 720 },
          height: { ideal: 1280 },
          aspectRatio: { ideal: PORTRAIT_CAPTURE_ASPECT },
          frameRate: { ideal: 30 },
        },
        audio: audioEnabled,
      },
    ]
    : [];
  const attempts = [
    ...portraitAttempts,
    ...landscapeAttempts,
    {
      video: { facingMode },
      audio: audioEnabled,
    },
    { video: true, audio: audioEnabled },
  ];
  let chain = Promise.reject(new Error('retry'));
  attempts.forEach((constraints) => {
    chain = chain.catch(() => navigator.mediaDevices.getUserMedia(constraints));
  });
  return chain;
}

/** @param {number} ms */
function formatRecordingElapsed(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${String(m)}:${String(rs).padStart(2, '0')}`;
}

/** @param {'camera' | 'camera_mic' | 'photos'} kind */
function describeMediaPermissionInstructions(kind) {
  const { isIOS, isAndroid } = Constants;
  if (isIOS) {
    const openLogictry =
      'On iPhone or iPad: open Settings, scroll down and tap Logictry. ';
    if (kind === 'photos') {
      return `${openLogictry}Tap Photos and choose All Photos or Limited Photos so the app can read your library. If you record video here, also enable Camera and Microphone on the same screen.`;
    }
    if (kind === 'camera_mic') {
      return `${openLogictry}Turn on Camera and Microphone.`;
    }
    return `${openLogictry}Turn on Camera.`;
  }
  if (isAndroid) {
    const base =
      'On Android: open Settings → Apps → Logictry → Permissions. ';
    if (kind === 'photos') {
      return `${base}Allow Photos and videos (or Files and media, depending on your phone). Turn on Camera and Microphone if you capture or record in the app.`;
    }
    if (kind === 'camera_mic') {
      return `${base}Allow Camera and Microphone.`;
    }
    return `${base}Allow Camera.`;
  }
  if (kind === 'photos') {
    return 'When your browser asks for files or photos, choose Allow. You can also open the site settings (often a lock icon next to the address bar) and allow access.';
  }
  if (kind === 'camera_mic') {
    return 'When your browser asks, allow camera and microphone for this site (site settings / permissions panel).';
  }
  return 'When your browser asks, allow camera access for this site (site settings / permissions panel).';
}

/**
 * @param {unknown} err
 * @param {'camera' | 'camera_mic'} hintKind — recording uses microphone too
 */
function describeMediaAccessFailure(err, hintKind) {
  const name =
    err && typeof err === 'object' && 'name' in err ? String(/** @type {{ name?: unknown }} */ (err).name) : '';
  const msg =
    err && typeof err === 'object' && 'message' in err ? String(/** @type {{ message?: unknown }} */ (err).message) : '';
  const combined = `${name} ${msg}`.trim();

  let title = hintKind === 'camera_mic' ? 'Camera or microphone unavailable' : 'Camera unavailable';
  let description =
    'Could not open the camera. Try again, pick Photo library, or upload from a desktop browser.';

  if (/getUserMedia unavailable/i.test(msg)) {
    return {
      title: 'Camera not available here',
      description:
        'This browser view does not support camera capture. Use the Logictry app or another browser, choose Photo library, or upload from a desktop.',
    };
  }

  const denied =
    name === 'NotAllowedError' ||
    name === 'PermissionDeniedError' ||
    /not allowed/i.test(msg) ||
    (/permission/i.test(combined.toLowerCase()) && /den(y|ied)|dismissed/i.test(combined.toLowerCase()));

  const notFound =
    name === 'NotFoundError' ||
    name === 'DevicesNotFoundError' ||
    /Requested device not found|Could not start video source/i.test(msg);

  const notReadable =
    name === 'NotReadableError' ||
    name === 'AbortError' ||
    name === 'TrackStartError';

  if (denied) {
    title =
      hintKind === 'camera_mic' ? 'Camera or microphone access denied' : 'Camera access denied';
    description = [
      hintKind === 'camera_mic'
        ? 'Recording needs access to your camera and microphone.'
        : 'Taking a photo needs access to your camera.',
      describeMediaPermissionInstructions(hintKind === 'camera_mic' ? 'camera_mic' : 'camera'),
      'Then return here and try again.',
    ].join(' ');
  } else if (notFound) {
    title = 'No usable camera';
    description =
      'No camera was found, or it may be in use by another app. Close other apps that use the camera, or choose Photo library or upload a file instead.';
  } else if (notReadable && !notFound) {
    description =
      'The camera could not be started (it may be busy or restricted). Try again, restart the app, or use Photo library.';
    if (Constants.isIOS || Constants.isAndroid) {
      description += ` ${describeMediaPermissionInstructions(hintKind === 'camera_mic' ? 'camera_mic' : 'camera')}`;
    }
  } else if (!err && (Constants.isIOS || Constants.isAndroid)) {
    description = [
      'Something blocked the camera.',
      describeMediaPermissionInstructions(hintKind === 'camera_mic' ? 'camera_mic' : 'camera'),
      'You can also tap Photo library to upload an existing image.',
    ].join(' ');
  }

  return { title, description };
}

const IMAGE_EXTENSIONS = [ '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.tiff', '.avif', '.bmp', '.heic', '.heif', '.ico', '.jfif', '.pjpeg', '.pjp' ];
const VIDEO_EXTENSIONS = [ 'mp4', 'mov', 'avi', 'wmv', 'flv', 'mkv', 'webm', 'ogv', 'm4v', 'm3u8', 'ts', '3gp', '3g2', 'mpeg', 'mpg', 'mpe' ];

/**
 * For video-only modes (e.g. stories): user-picked file must be a video.
 * In-app/recorded WebM is handled separately via `isBlockedUploadContainerFile` bypass.
 */
function isUserPickVideoFile(file) {
  if (!file) return false;
  const t = typeof file.type === 'string' ? file.type.trim().toLowerCase() : '';
  if (/^video\//.test(t)) return true;
  const base = typeof file.name === 'string' ? file.name.split(/[?#]/)[0].toLowerCase() : '';
  return VIDEO_EXTENSIONS.some((ext) => base.endsWith(`.${ext}`));
}

function maxUploadBytesForFile(file) {
  return isUserPickVideoFile(file) ? MAX_VIDEO_UPLOAD_BYTES : MAX_FILE_UPLOAD_BYTES;
}

function uploadTooLargeErrorDescription(file) {
  if (isUserPickVideoFile(file)) {
    return 'File size too large (50 MB max for video). Only approved creators can upload larger files.';
  }
  return 'File size too large (25 MB max size). Only approved creators can upload files larger than 25 MB.';
}

/** Disallowed uploads (chosen files); in-app recorder may still emit WebM and bypasses this in `__uploadFile`. */
const BLOCKED_UPLOAD_EXT_RE = /\.(webm|ogv)(\?|#|$)/i;
function isBlockedUploadContainerFile(file) {
  if (!file) return false;
  const nm = typeof file.name === 'string' ? file.name.toLowerCase() : '';
  if (nm && BLOCKED_UPLOAD_EXT_RE.test(nm.split('#')[0] || nm)) return true;
  const t = typeof file.type === 'string' ? file.type.trim().toLowerCase() : '';
  if (/^video\/webm$/.test(t) || /^video\/ogg$/.test(t) || /^application\/ogg$/.test(t)) return true;
  return false;
}

const FullscreenCameraRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 5;
  background: #000;
  box-sizing: border-box;
`;
/** Story capture: visible 9:16 frame on desktop landscape viewports. */
const PortraitCameraViewport = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
`;
const PortraitCameraVideo = styled.video`
  height: 100%;
  max-height: 100vh;
  width: auto;
  max-width: 100vw;
  aspect-ratio: 9 / 16;
  object-fit: cover;
`;
const NativePortraitStoryVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
`;

const CameraPurposeToggleWrap = styled.div`
  position: absolute;
  top: max(10vh, 3rem);
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  padding: 4px;
  gap: 0;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.32);
  z-index: 3;
`;
const CameraPurposeToggleBtn = styled.button`
  appearance: none;
  border: 0;
  margin: 0;
  padding: 0.4rem 0.98rem;
  border-radius: 999px;
  font-size: ${Constants.SemiSmallFontSize};
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$active ? 'rgba(255, 255, 255, 0.93)' : 'transparent')};
  color: ${(p) => (p.$active ? '#111111' : 'rgba(255, 255, 255, 0.92)')};
  transition: background 0.15s ease, color 0.15s ease;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const RecordingElapsed = styled.div`
  position: absolute;
  top: max(17vh, 5.85rem);
  left: 50%;
  transform: translateX(-50%);
  padding: 0.28rem 0.72rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #ffffff;
  font-size: ${Constants.SemiSmallFontSize};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.05em;
  z-index: 3;
`;
const RecordButton = styled.div`
  position: absolute;
  bottom: 10vh;
  left: calc(50vw - 40px);
  border: 8px solid rgba(255, 255, 255, 0.4);
  height: 80px;
  width: 80px;
  border-radius: 50%;
  > button {
    border-radius: 50%;
    width: 64px;
    height: 64px;
    background: rgba(227, 73, 28, 1);
  }
`;
const RecordingText = styled.div`
  color: white;
`;
const CloseButton = styled.div`
  position: absolute;
  top: calc(0.25rem + var(--logictry-popup-safe-top, 0px));
  right: 1rem;
  font-size: 2rem;
  color: white;
  cursor: pointer;
`;
const FlipCameraButton = styled.button`
  position: absolute;
  top: calc(0.35rem + var(--logictry-popup-safe-top, 0px));
  left: 0.75rem;
  z-index: 4;
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 999px;
  padding: 0.42rem 0.82rem;
  background: rgba(0, 0, 0, 0.38);
  color: #fff;
  font-size: ${Constants.SemiSmallFontSize};
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;
const ReviewForm = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 5;
  padding: 1.25rem 1rem 1.75rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.92) 0%, rgba(0, 0, 0, 0.55) 55%, transparent 100%);
  box-sizing: border-box;
`;
/** Centers the story review UI in a 9:16 frame (matches LogicBaseStoryViewer). */
const StoryReviewShell = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
`;
const StoryReviewFrame = styled.div`
  position: relative;
  width: 100%;
  max-width: 375px;
  height: 100%;
  max-height: 667px;
  aspect-ratio: 9 / 16;
  background: #000;
  overflow: hidden;
  border-radius: 0.75rem;
  ${Constants.MediaMobile} {
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
  }
`;
const ReviewPreviewVideo = styled(PortraitCameraVideo)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  object-fit: cover;
`;
const NativePortraitStoryReviewVideo = styled(NativePortraitStoryVideo)`
  position: absolute;
  inset: 0;
`;
const ReviewCloseButton = styled(CloseButton)`
  z-index: 5;
  right: 0.5rem;
`;
const ReviewField = styled.input`
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 0.55rem;
  padding: 0.55rem 0.7rem;
  border-radius: 0.45rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: ${Constants.SemiSmallFontSize};
  &::placeholder {
    color: rgba(255, 255, 255, 0.55);
  }
`;
const ReviewTextarea = styled.textarea`
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 0.75rem;
  padding: 0.55rem 0.7rem;
  border-radius: 0.45rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: ${Constants.SemiSmallFontSize};
  min-height: 3.25rem;
  resize: vertical;
  font-family: inherit;
  &::placeholder {
    color: rgba(255, 255, 255, 0.55);
  }
`;
const ReviewActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;
const ReviewActionBtn = styled.button`
  flex: 1 1 auto;
  min-width: 5.5rem;
  appearance: none;
  border: 0;
  border-radius: 999px;
  padding: 0.62rem 0.85rem;
  font-size: ${Constants.SemiSmallFontSize};
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$primary ? Constants.PrimaryColor : 'rgba(255, 255, 255, 0.14)')};
  color: #fff;
  border: 1px solid ${(p) => (p.$primary ? 'transparent' : 'rgba(255, 255, 255, 0.35)')};
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
const ReviewHeading = styled.div`
  color: #fff;
  font-size: ${Constants.SemiSmallFontSize};
  font-weight: 600;
  margin-bottom: 0.65rem;
  text-align: center;
`;
const UploadChoicePanel = styled.div`
  width: 100%;
  max-width: min(460px, calc(100vw - 4.5rem));
  margin: 0 auto;
  padding: 1.75rem 2rem 2rem;
  box-sizing: border-box;
`;
const UploadMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.78rem 0.95rem;
  margin-bottom: 0.55rem;
  border-radius: 0.55rem;
  border: ${Constants.LightBorder};
  box-sizing: border-box;
  background: #fff;
  color: ${Constants.DarkTextColor};
  font-size: ${Constants.SemiSmallFontSize};
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    border-color: ${Constants.PrimaryColor};
    box-shadow: 0 0 0 3px color-mix(in srgb, ${Constants.PrimaryColor} 22%, transparent);
  }
`;
const HoverDiv = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  /* Desktop only: touch devices don't get hover until after first tap — causes double-tap to open menus. */
  @media (hover: hover) and (pointer: fine) {
    &:hover div {
      opacity: 1 !Important;
    }
  }
`;
const ProfilePic = styled.div`
  position: relative;
  text-align: center;
  background-repeat: no-repeat;
  overflow: hidden;
  background-position: center;
  cursor: pointer;

  > * {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  @media (hover: none), (pointer: coarse) {
    > * {
      opacity: 1;
    }
  }
`;
const DeleteButton = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  left: unset;
  color: ${Constants.White};
  opacity: 0;

  @media (hover: none), (pointer: coarse) {
    opacity: 1;
  }
`;
const UploadOverlay = styled.div`
  
`;
const DisableUploadOverlay = styled.div`
  cursor: pointer;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;
const FileName = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 0;
  padding: 0.5rem;
  > div {
    max-width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
const Iframe = styled.div`
  // transform: scale(0.25, 0.25);
  // transform-origin: top left;
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  iframe {
    width: 100%;
    height: 100%;
  }
`;
const PopupWrapper = styled.div`
  position: fixed;
  top: 3rem;
  left: 3rem;
  width: calc(100% - 6rem);
  height: calc(100% - 6rem);
  ${Constants.MediaMobileAndTablet} {
    left: 1rem;
    width: calc(100% - 2rem);
    height: calc(100% - 4rem);
  }
  > *:first-child {
    height: 100%;
    width: 100%;
  }
  > div:last-child {
    position: fixed;
    top: 0rem;
    left: 0rem;
    right: 0rem;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 3rem;
  }
`;

export default class MediaUploadTile extends LogictryComponent {
  static propTypes = {
    backgroundImage: PropTypes.string,
    backgroundColor: PropTypes.string,
    onUploadQueued: PropTypes.func,
    onUploadFinish: PropTypes.func,
    style: PropTypes.object,
    disableEditing: PropTypes.bool,
    /** When true with `disableEditing`, taps do not open the read-only asset preview / copy popup (e.g. avatars in lists). */
    suppressReadonlyAssetPreview: PropTypes.bool,
    hideUploadText: PropTypes.bool,
    allowDeleting: PropTypes.bool,
    onDelete: PropTypes.func,
    disableUpload: PropTypes.bool,
    s3Path: PropTypes.string,
    signingUrl: PropTypes.string,
    additionalQueryParams: PropTypes.object,
    fileType: PropTypes.string,
    onlyAllowImage: PropTypes.bool,
    /** When true, only video capture and video file picks (e.g. stories). */
    onlyAllowVideo: PropTypes.bool,
    queueUpload: PropTypes.bool,
    uploadChoiceMenu: PropTypes.bool,
    /** Optional icon shown in compact (small) upload tiles instead of the default upload glyph. */
    uploadIcon: PropTypes.node,
    onRequestFolderUpload: PropTypes.func,
    /** When true (non-menu mode), the tile only triggers `onRequestFolderUpload` — used by Content Source folder uploads. */
    folderPickOnly: PropTypes.bool,
    /** When true, opens fullscreen video camera (record mode, mic on) on mount — e.g. a new story. */
    autoOpenVideoCamera: PropTypes.bool,
    /** Hidden hosts (e.g. profile New Story): parent calls `openVideoCameraForRecording` on tap; omit invisible tap targets. */
    suppressTapTarget: PropTypes.bool,
    /**
     * Stories: 9:16 preview + center-crop recording on desktop; `.portrait.` transcode tag on
     * in-app recordings only (file picks keep viewport-based naming).
     */
    forcePortraitVideoCapture: PropTypes.bool,
    /** Fired when the fullscreen camera session ends without a completed upload (close, discard, or after error OK). Not fired when `closeVideo({ suppressDismiss: true })` is used (e.g. after upload). */
    onFullscreenCameraDismiss: PropTypes.func,
    /** When true, chosen video files open a review screen before upload — e.g. stories. */
    reviewBeforeUpload: PropTypes.bool,
    /** When true (default), in-app camera recordings open a review screen before upload. */
    reviewRecordedVideo: PropTypes.bool,
    /** Asset createdTime. While it is inside the transcode window, the card shows a processing message instead of the player. */
    createdTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),
  }
  constructor(props) {
    super();
    if (props.folderPickOnly) this.UploadText = 'Upload folder';
    else if (props.onlyAllowVideo) this.UploadText = 'Upload video';
    else this.UploadText = props.onlyAllowImage ? 'Upload New Image' : 'Upload New File';
    this.state = {
      overlayText: this.UploadText,
      imagePreview: null,
      showOverlay: false,
      error: false,
      errorDescription: '',
      showVideoCamera: false,
      cameraState: 'unstarted',
      showAssetPopup: false,
      nodeRef: null,
      contentType: null,
      showUploadMenu: false,
      cameraPurpose: 'record',
      recordingElapsedMs: 0,
      cameraFacingMode: 'user',
      reviewTitle: '',
      reviewDescription: '',
    }
    // Set to run after this.props exists
    setTimeout(() => { this.getContentType(); });
  }
  componentDidMount() {
    const {
      autoOpenVideoCamera, disableEditing, disableUpload, onlyAllowImage, folderPickOnly,
    } = this.props;
    if (
      autoOpenVideoCamera
      && !disableEditing
      && !disableUpload
      && !onlyAllowImage
      && !folderPickOnly
    ) {
      this._autoOpenVideoCameraTimeout = setTimeout(() => {
        this._autoOpenVideoCameraTimeout = null;
        this.openVideoCameraForRecording();
      }, 0);
    }
    this.scheduleTranscodeProcessingEnd();
  }
  componentDidUpdate(prevProps) {
    const prevCreated = prevProps.createdTime && new Date(prevProps.createdTime).getTime();
    const nextCreated = this.props.createdTime && new Date(this.props.createdTime).getTime();
    if (prevCreated !== nextCreated || prevProps.backgroundImage !== this.props.backgroundImage) {
      this.scheduleTranscodeProcessingEnd();
    }
  }
  componentWillUnmount() {
    this.clearTranscodeProcessingTimer();
    if (this._autoOpenVideoCameraTimeout != null) {
      clearTimeout(this._autoOpenVideoCameraTimeout);
      this._autoOpenVideoCameraTimeout = null;
    }
    CreateProject.unqueueMediaUploadTile(this);
    this.clearRecordingClock();
    this.stopPortraitCaptureLoop();
    this.closeVideo({ suppressDismiss: true });
    this.clearReviewDraft();
  }
  clearTranscodeProcessingTimer = () => {
    if (this._transcodeProcessingTimer != null) {
      clearTimeout(this._transcodeProcessingTimer);
      this._transcodeProcessingTimer = null;
    }
  }
  scheduleTranscodeProcessingEnd = () => {
    this.clearTranscodeProcessingTimer();
    const { createdTime, backgroundImage } = this.props;
    if (!uploadedVideoNeedsTranscode(backgroundImage)) return;
    const remaining = videoTranscodeProcessingRemaining(createdTime);
    if (remaining <= 0) return;
    this._transcodeProcessingTimer = setTimeout(() => {
      this._transcodeProcessingTimer = null;
      this.forceUpdate();
    }, remaining);
  }
  clearReviewDraft = () => {
    if (this._reviewObjectUrl) {
      URL.revokeObjectURL(this._reviewObjectUrl);
      this._reviewObjectUrl = null;
    }
    this._reviewVideoFile = null;
    this._reviewFromRecorder = false;
  }
  isStoryReviewMode = () => {
    const { onlyAllowVideo, reviewBeforeUpload, forcePortraitVideoCapture } = this.props;
    return !!(onlyAllowVideo && (reviewBeforeUpload || forcePortraitVideoCapture));
  }
  shouldReviewAfterRecording = () => {
    const { reviewBeforeUpload, reviewRecordedVideo = true } = this.props;
    return !!(reviewBeforeUpload || reviewRecordedVideo);
  }
  showReviewForFile = (file, { fromRecorder = false } = {}) => {
    this.clearReviewDraft();
    this._reviewVideoFile = file;
    this._reviewFromRecorder = fromRecorder;
    this._reviewObjectUrl = URL.createObjectURL(file);
    this.setState({
      showVideoCamera: true,
      cameraState: 'review',
      cameraPurpose: 'record',
      reviewTitle: '',
      reviewDescription: '',
      recordingElapsedMs: 0,
    });
  }
  onReviewTitleChange = (event) => {
    this.setState({ reviewTitle: event.target.value });
  }
  onReviewDescriptionChange = (event) => {
    this.setState({ reviewDescription: event.target.value });
  }
  retakeReview = () => {
    this.clearReviewDraft();
    this.setState({
      reviewTitle: '',
      reviewDescription: '',
      showVideoCamera: false,
      cameraState: 'unstarted',
    }, () => {
      this.openVideoCameraForRecording();
    });
  }
  discardReview = () => {
    this.clearReviewDraft();
    this.setState({
      showVideoCamera: false,
      cameraState: 'unstarted',
      reviewTitle: '',
      reviewDescription: '',
    });
    const { onFullscreenCameraDismiss } = this.props;
    if (onFullscreenCameraDismiss) onFullscreenCameraDismiss();
  }
  approveReview = () => {
    const file = this._reviewVideoFile;
    if (!file) return;
    const title = (this.state.reviewTitle || '').trim();
    const description = (this.state.reviewDescription || '').trim();
    if (this._reviewFromRecorder) {
      this._bypassBlockedContainerUploadCheck = true;
    }
    this.clearReviewDraft();
    this._pendingReviewMeta = this.isStoryReviewMode()
      ? { title, description }
      : { description };
    this.setState({
      showVideoCamera: false,
      cameraState: 'unstarted',
      reviewTitle: '',
      reviewDescription: '',
      overlayText: '0%',
      showOverlay: true,
    });
    this.__uploadFile(file);
  }
  fileLoaded = false;
  _finalizeRecorderUpload = false;
  /** When true next `__uploadFile` skips WebM/Ogv block (MediaRecorder output). Cleared immediately in `__uploadFile`. */
  _bypassBlockedContainerUploadCheck = false;
  showUploadDisabledPopup = () => {
    this.setState({ error: 'Upload disabled', errorDescription: 'You must be logged in', overlayText: this.UploadText, imagePreview: null, showOverlay: false });
  }
  pickRecorderMime = () => {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
      if (MediaRecorder.isTypeSupported('video/mp4')) return { mimeType: 'video/mp4', ext: '.mp4' };
      if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) return { mimeType: 'video/webm;codecs=vp9', ext: '.webm' };
      if (MediaRecorder.isTypeSupported('video/webm')) return { mimeType: 'video/webm', ext: '.webm' };
    }
    return { mimeType: 'video/webm', ext: '.webm' };
  }
  closeUploadMenu = () => this.setState({ showUploadMenu: false });
  clearRecordingClock = () => {
    if (this._recordingElapsedInterval != null) {
      clearInterval(this._recordingElapsedInterval);
      this._recordingElapsedInterval = null;
    }
    this._recordingStartedAtMs = null;
  }
  isForcePortraitCapture = () => !!this.props.forcePortraitVideoCapture
  /** Story capture — 60s recording cap applies here only. */
  isStoryVideoCapture = () => this.isForcePortraitCapture()
  storyMaxRecordingMs = () => (this.isStoryVideoCapture() ? STORY_MAX_RECORDING_MS : null)
  cameraMediaOptions = () => ({ forcePortrait: this.isForcePortraitCapture() })
  stopRecordingAndUpload = () => {
    if (!this.videoMediaRecorder || this.state.cameraState !== 'recording') return;
    this._finalizeRecorderUpload = true;
    try {
      this.videoMediaRecorder.stop();
    } catch {
      //
    }
    this.setState({ cameraState: 'stopping' });
  }
  tickRecordingElapsed = () => {
    const startAt = this._recordingStartedAtMs;
    if (startAt == null || this.state.cameraState !== 'recording') return;
    const elapsed = Date.now() - startAt;
    this.setState({ recordingElapsedMs: elapsed });
    const maxMs = this.storyMaxRecordingMs();
    if (maxMs != null && elapsed >= maxMs) {
      this.stopRecordingAndUpload();
    }
  }
  stopPortraitCaptureLoop = () => {
    if (this._portraitCaptureRaf != null) {
      cancelAnimationFrame(this._portraitCaptureRaf);
      this._portraitCaptureRaf = null;
    }
  }
  ensurePortraitRecordCanvas = () => {
    if (!this._portraitRecordCanvas) {
      this._portraitRecordCanvas = document.createElement('canvas');
    }
    this._portraitRecordCanvas.width = PORTRAIT_RECORD_WIDTH;
    this._portraitRecordCanvas.height = PORTRAIT_RECORD_HEIGHT;
    return this._portraitRecordCanvas;
  }
  drawPortraitRecordFrame = () => {
    const vid = this.videoCanvas;
    const canvas = this._portraitRecordCanvas;
    if (!vid || !canvas || !vid.videoWidth) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const mirror = isSelfieFacingMode(this.state.cameraFacingMode);
    const needsPortraitCrop = this.isForcePortraitCapture() && !isNativePortraitStoryCapture(true);
    if (needsPortraitCrop) {
      canvas.width = PORTRAIT_RECORD_WIDTH;
      canvas.height = PORTRAIT_RECORD_HEIGHT;
    } else {
      canvas.width = vid.videoWidth;
      canvas.height = vid.videoHeight;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (mirror) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    if (needsPortraitCrop) {
      const { sx, sy, sw, sh } = portraitCropSourceRect(vid.videoWidth, vid.videoHeight);
      ctx.drawImage(vid, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    } else {
      ctx.drawImage(vid, 0, 0);
    }
  }
  startPortraitCaptureLoop = () => {
    this.stopPortraitCaptureLoop();
    const tick = () => {
      this.drawPortraitRecordFrame();
      this._portraitCaptureRaf = requestAnimationFrame(tick);
    };
    this._portraitCaptureRaf = requestAnimationFrame(tick);
  }
  /** @returns {MediaStream | null} */
  buildRecorderInputStream = () => {
    if (!this.videoCameraStream) return null;
    const needsPortraitCrop = this.isForcePortraitCapture() && !isNativePortraitStoryCapture(true);
    const needsSelfieMirror = isSelfieFacingMode(this.state.cameraFacingMode);
    if (!needsPortraitCrop && !needsSelfieMirror) {
      return this.videoCameraStream;
    }
    const canvas = this.ensurePortraitRecordCanvas();
    this.drawPortraitRecordFrame();
    this.startPortraitCaptureLoop();
    const canvasStream = canvas.captureStream(30);
    const audioTrack = this.videoCameraStream.getAudioTracks()[0];
    if (audioTrack) canvasStream.addTrack(audioTrack);
    return canvasStream;
  }
  notifyCameraUnavailable = (err, hintKind = 'camera') => {
    this.clearRecordingClock();
    const { title, description } = describeMediaAccessFailure(err, hintKind);
    return this.setState({
      cameraState: 'unstarted',
      showVideoCamera: false,
      cameraPurpose: 'record',
      error: title,
      errorDescription: description,
      overlayText: this.UploadText,
      imagePreview: null,
      recordingElapsedMs: 0,
      cameraFacingMode: 'user',
    });
  }
  menuUseCamera = (ev) => {
    ev?.preventDefault();
    ev?.stopPropagation();
    if (this.props.onlyAllowVideo) {
      return this.menuRecordVideo(ev);
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      this.closeUploadMenu();
      return this.notifyCameraUnavailable(new Error('getUserMedia unavailable'), 'camera');
    }
    this.closeUploadMenu();
    requestCameraMedia(false, this.state.cameraFacingMode, this.cameraMediaOptions()).then((stream) => {
      this.videoCameraStream = stream;
      this.setState({
        showVideoCamera: true,
        cameraState: 'started',
        cameraPurpose: 'photo',
      }, () => {
        requestAnimationFrame(() => {
          if (this.videoCanvas) this.videoCanvas.srcObject = stream;
        });
      });
    }).catch((err) => this.notifyCameraUnavailable(err, 'camera'));
  }
  flipCameraFacing = () => {
    if (this.state.cameraState === 'recording') return;
    if (!navigator.mediaDevices?.getUserMedia) return;
    const prevFacing = this.state.cameraFacingMode;
    const nextFacing = prevFacing === 'user' ? 'environment' : 'user';
    const audio = this.state.cameraPurpose === 'record';

    if (this.videoCameraStream) {
      this.videoCameraStream.getTracks().forEach((t) => t.stop());
      this.videoCameraStream = null;
    }
    if (this.videoCanvas) this.videoCanvas.srcObject = null;

    requestCameraMedia(audio, nextFacing, this.cameraMediaOptions()).then((stream) => {
      this.videoCameraStream = stream;
      this.setState({ cameraFacingMode: nextFacing, cameraState: 'started' }, () => {
        requestAnimationFrame(() => {
          if (this.videoCanvas) this.videoCanvas.srcObject = stream;
        });
      });
    }).catch((err) => {
      requestCameraMedia(audio, prevFacing, this.cameraMediaOptions()).then((stream) => {
        this.videoCameraStream = stream;
        this.setState({ cameraFacingMode: prevFacing, cameraState: 'started' }, () => {
          requestAnimationFrame(() => {
            if (this.videoCanvas) this.videoCanvas.srcObject = stream;
          });
        });
      }).catch((errInner) => this.notifyCameraUnavailable(errInner || err, audio ? 'camera_mic' : 'camera'));
    });
  }
  switchCameraPurpose = (nextPurpose) => {
    if (nextPurpose === 'photo' && this.props.onlyAllowVideo) return;
    const { cameraState, cameraPurpose, showVideoCamera } = this.state;
    if (!showVideoCamera || cameraState === 'recording' || cameraPurpose === nextPurpose) return;
    if (!navigator.mediaDevices?.getUserMedia) return;
    if (this.videoCameraStream) {
      this.videoCameraStream.getTracks().forEach((t) => t.stop());
      this.videoCameraStream = null;
    }
    if (this.videoCanvas) this.videoCanvas.srcObject = null;
    const audio = nextPurpose === 'record';
    requestCameraMedia(audio, this.state.cameraFacingMode, this.cameraMediaOptions()).then((stream) => {
      this.videoCameraStream = stream;
      this.setState({ cameraPurpose: nextPurpose, cameraState: 'started' }, () => {
        requestAnimationFrame(() => {
          if (this.videoCanvas) this.videoCanvas.srcObject = stream;
        });
      });
    }).catch((err) => this.notifyCameraUnavailable(err, audio ? 'camera_mic' : 'camera'));
  }
  menuTakePhoto = (ev) => {
    ev?.preventDefault();
    ev?.stopPropagation();
    if (this.props.onlyAllowVideo) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      this.closeUploadMenu();
      if (NativeCamera.avoidWebKitFileInputFallback()) {
        return this.notifyCameraUnavailable(new Error('getUserMedia unavailable'), 'camera');
      }
      setTimeout(() => this._capturePhotoInput?.click(), 0);
      return;
    }
    this.closeUploadMenu();
    requestCameraMedia(false, this.state.cameraFacingMode).then((stream) => {
      this.videoCameraStream = stream;
      this.setState({
        showVideoCamera: true,
        cameraState: 'started',
        cameraPurpose: 'photo',
      }, () => {
        requestAnimationFrame(() => {
          if (this.videoCanvas) this.videoCanvas.srcObject = stream;
        });
      });
    }).catch((err) => this.notifyCameraUnavailable(err, 'camera'));
  }
  menuChooseFile = () => {
    this.closeUploadMenu();
    if (NativeCamera.avoidWebKitFileInputFallback()) {
      return this.setState({
        error: 'Choose file unavailable',
        errorDescription: 'The system file browser isn’t used in the native app. Use Photo library or Take photo—or open Logictry in a desktop browser.',
        overlayText: this.UploadText,
        imagePreview: null,
      });
    }
    setTimeout(() => this._filePickInput?.click(), 0);
  }
  /**
   * Capacitor/native gallery when registered; otherwise `image/*` file input (web / non-Ionic).
   * When `avoidWebKitFileInputFallback()` is true (native app), never falls through to `<input type="file">`.
   */
  triggerPhotoLibraryPick = async () => {
    const file = await NativeCamera.pickGalleryImageFile();
    const galleryFailure = NativeCamera.takeLastGalleryPickFailure();
    if (file) {
      this.uploadExistingFile({ target: { files: [file] } });
      return;
    }
    if (NativeCamera.avoidWebKitFileInputFallback()) {
      if (galleryFailure === 'permission_denied') {
        return this.setState({
          error: 'Photos access needed',
          errorDescription: [
            'Logictry needs permission to read your photo library.',
            describeMediaPermissionInstructions('photos'),
            'Then open Photo library again.',
          ].join(' '),
          overlayText: this.UploadText,
          imagePreview: null,
        });
      }
      if (galleryFailure === 'unknown') {
        return this.setState({
          error: 'Could not open photo library',
          errorDescription: [
            'Something went wrong opening your photos.',
            describeMediaPermissionInstructions('photos'),
            'If this keeps happening, try restarting the app.',
          ].join(' '),
          overlayText: this.UploadText,
          imagePreview: null,
        });
      }
      return;
    }
    setTimeout(() => {
      const input = this._photoLibraryInput || this._touchPhotoLibraryFallbackInput;
      input?.click();
    }, 0);
  }
  menuChoosePhotoLibrary = async () => {
    this.closeUploadMenu();
    if (this.props.onlyAllowVideo) return;
    await this.triggerPhotoLibraryPick();
  }
  /** Fullscreen camera with mic, ready to record (same path as “Record video” in the add-media menu). */
  openVideoCameraForRecording = () => {
    if (this.state.showVideoCamera) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      return this.notifyCameraUnavailable(new Error('getUserMedia unavailable'), 'camera_mic');
    }
    this.closeUploadMenu();
    requestCameraMedia(true, this.state.cameraFacingMode, this.cameraMediaOptions()).then((stream) => {
      this.videoCameraStream = stream;
      this.setState({
        showVideoCamera: true,
        cameraState: 'started',
        cameraPurpose: 'record',
      }, () => {
        requestAnimationFrame(() => {
          if (this.videoCanvas) this.videoCanvas.srcObject = stream;
        });
      });
    }).catch((err) => this.notifyCameraUnavailable(err, 'camera_mic'));
  }
  menuRecordVideo = (ev) => {
    ev?.preventDefault();
    ev?.stopPropagation();
    this.openVideoCameraForRecording();
  }
  menuUploadFolder = () => {
    const { onRequestFolderUpload } = this.props;
    if (onRequestFolderUpload) onRequestFolderUpload();
    this.closeUploadMenu();
  }
  uploadExistingFileAndReset = (e) => {
    this.uploadExistingFile(e);
    if (e.target) e.target.value = '';
  }
  captureStillPhotoFromPreview = () => {
    if (this.props.onlyAllowVideo) return;
    const vid = this.videoCanvas;
    if (!vid || !vid.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = vid.videoWidth;
    canvas.height = vid.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (isSelfieFacingMode(this.state.cameraFacingMode)) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(vid, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      this.closeVideo({ suppressDismiss: true });
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      this.__uploadFile(file);
    }, 'image/jpeg', 0.92);
  }
  toggleRecording = () => {
    if (this.state.cameraPurpose !== 'record') return;
    if (!this.videoCameraStream) return;
    if (typeof MediaRecorder === 'undefined') {
      return this.setState({ error: 'Recording not supported', errorDescription: 'This browser does not support video recording.', overlayText: this.UploadText, imagePreview: null });
    }
    if (this.state.cameraState === 'started') {
      let mimeType;
      let ext;
      try {
        ({ mimeType, ext } = this.pickRecorderMime());
        this._finalizeRecorderUpload = false;
        const recordStream = this.buildRecorderInputStream();
        if (!recordStream) {
          throw new Error('No camera stream');
        }
        this.videoMediaRecorder = new MediaRecorder(recordStream, { mimeType });
        this.videoBlobRecorded = [];

        this.videoMediaRecorder.addEventListener('dataavailable', (e) => {
          this.videoBlobRecorded.push(e.data);
        });

        this.videoMediaRecorder.addEventListener('stop', () => {
          this.clearRecordingClock();
          this.stopPortraitCaptureLoop();
          const shouldUpload = this._finalizeRecorderUpload;
          this._finalizeRecorderUpload = false;
          const chunks = this.videoBlobRecorded;
          if (this.videoCameraStream) {
            this.videoCameraStream.getTracks().forEach((t) => t.stop());
          }
          this.videoCameraStream = null;
          this.videoMediaRecorder = null;
          this.videoBlobRecorded = [];
          this.setState({ cameraState: 'unstarted', showVideoCamera: false, cameraPurpose: 'record', recordingElapsedMs: 0, cameraFacingMode: 'user' });
          if (!shouldUpload || !chunks.length) {
            const { onFullscreenCameraDismiss } = this.props;
            if (onFullscreenCameraDismiss) onFullscreenCameraDismiss();
            return;
          }
          const dateNow = Date.now();
          const portrait = this.isForcePortraitCapture() || captureViewportVideoOrientationHint() === 'portrait';
          const filename = portrait ? `${dateNow}.portrait${ext}` : `${dateNow}${ext}`;
          const body = new File(chunks, filename, { type: mimeType });
          if (this.shouldReviewAfterRecording()) {
            this.showReviewForFile(body, { fromRecorder: true });
            return;
          }
          this._bypassBlockedContainerUploadCheck = true;
          this.__uploadFile(body);
        });

        this.videoMediaRecorder.start(1000);
        this.clearRecordingClock();
        this._recordingStartedAtMs = Date.now();
        this._recordingElapsedInterval = setInterval(this.tickRecordingElapsed, 300);
        this.setState({ cameraState: 'recording', recordingElapsedMs: 0 });
      } catch {
        this.clearRecordingClock();
        this.stopPortraitCaptureLoop();
        if (this.videoCameraStream) {
          this.videoCameraStream.getTracks().forEach((t) => t.stop());
          this.videoCameraStream = null;
        }
        this.videoMediaRecorder = null;
        this.videoBlobRecorded = [];
        this.setState({
          cameraState: 'unstarted',
          showVideoCamera: false,
          cameraPurpose: 'record',
          recordingElapsedMs: 0,
          cameraFacingMode: 'user',
          error: 'Recording failed',
          errorDescription: 'Could not start recorder. Try uploading a file instead.',
          overlayText: this.UploadText,
          imagePreview: null,
        });
      }
    } else if (this.state.cameraState === 'recording') {
      this.stopRecordingAndUpload();
    }
  }
  closeVideo = (opts = {}) => {
    if (this.state.cameraState === 'review') {
      if (opts.suppressDismiss) {
        this.clearReviewDraft();
        this.setState({
          showVideoCamera: false,
          cameraState: 'unstarted',
          reviewTitle: '',
          reviewDescription: '',
        });
        return;
      }
      this.discardReview();
      return;
    }
    this._finalizeRecorderUpload = false;
    this.clearRecordingClock();
    this.stopPortraitCaptureLoop();
    if (this.videoMediaRecorder && this.state.cameraState === 'recording') {
      try {
        this.videoMediaRecorder.stop();
      } catch {
        //
      }
      this.setState({ cameraState: 'stopping' });
      return;
    }
    if (this.videoCameraStream) {
      this.videoCameraStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.videoCameraStream = null;
    }
    this.videoMediaRecorder = null;
    this.videoBlobRecorded = [];
    this.setState({
      cameraState: 'unstarted',
      showVideoCamera: false,
      cameraPurpose: 'record',
      recordingElapsedMs: 0,
      cameraFacingMode: 'user',
    });
    const { suppressDismiss } = opts;
    const { onFullscreenCameraDismiss } = this.props;
    if (!suppressDismiss && onFullscreenCameraDismiss) onFullscreenCameraDismiss();
  }
  uploadExistingFile = async (e) => {
    const { queueUpload, onUploadQueued, onlyAllowVideo } = this.props;
    const s = e.target.files[0];
    if (!s) return;

    if (onlyAllowVideo && !isUserPickVideoFile(s)) {
      if (e.target) e.target.value = '';
      return this.setState({
        error: 'Video only',
        errorDescription: 'Stories support video only. Record a clip or choose a video file.',
        overlayText: this.UploadText,
        imagePreview: null,
        showOverlay: false,
      });
    }
    if (isBlockedUploadContainerFile(s)) {
      if (e.target) e.target.value = '';
      return this.setState({
        error: 'File type not allowed',
        errorDescription: 'WebM and Ogg (OGV) uploads are disabled. Choose MP4, MOV, or M4V, or use Record video.',
        overlayText: this.UploadText,
        imagePreview: null,
        showOverlay: false,
      });
    }

    // Show image preview
    if (s.type.includes('image')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (this.state.errorDescription) return;
        this.setState({
          imagePreview: (reader.result)
        });
      };
      reader.readAsDataURL(s);
    }

    if (e.target) e.target.value = '';
    const { reviewBeforeUpload } = this.props;
    if (reviewBeforeUpload && onlyAllowVideo) {
      this.showReviewForFile(s, { fromRecorder: false });
      return;
    }

    if (queueUpload) {
      this.setState({
        overlayText: `${s.name}`,
        showOverlay: true,
      });
      CreateProject.queueMediaUploadTile(this);
      this.queuedFile = s;
      if (onUploadQueued) onUploadQueued(true);
    } else {
      this.setState({
        overlayText: `0%`,
        showOverlay: true,
      });
      this.__uploadFile(s);
    }
  }
  resumeUpload = (user) => {
    if (!this.queuedFile) return Promise.resolve(false);
    return new Promise((resolve) => {
      this.queuedResolve = resolve;
      this.queuedUser = user;
      this.__uploadFile(this.queuedFile);
      this.queuedFile = null;
      this.queuedNext = null;
    });
  }
  /** Settle a queued-submit upload that failed so the submit lock can release. */
  failQueuedUpload = () => {
    if (!this.queuedResolve) return;
    const resolve = this.queuedResolve;
    this.queuedResolve = null;
    this.queuedFile = null;
    this.queuedNext = null;
    this.queuedUser = null;
    resolve(false);
  }
  getSignedUrl = async (file, callback) => {
    let { s3Path, signingUrl, additionalQueryParams } = this.props;
    if (this.queuedUser) s3Path = s3Path.replace(`${LOGGED_OUT_USER_ID}/`, `${this.queuedUser}/`);
    let uri = `${Constants.logictryOrigin}${signingUrl}?objectName=${this.hashFilename}/${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type)}&path=${s3Path}`;
    Object.keys(additionalQueryParams || {}).forEach((key) => {
      uri = `${uri}&${key}=${encodeURIComponent(additionalQueryParams[key])}`;
    });
    const pendingDescription = this._pendingReviewMeta?.description;
    if (pendingDescription) {
      uri = `${uri}&description=${encodeURIComponent(pendingDescription)}`;
    }

    if (this.queuedUser) uri = `${uri}&owner=${this.queuedUser}`;
    const result = await Server.__request({
      uri,
      method: 'GET',
      json: true,
    });
    if (!result) return this.failQueuedUpload();
    callback(result.body);
  }
  __uploadFile(s) {
    const bypassBlock = this._bypassBlockedContainerUploadCheck;
    this._bypassBlockedContainerUploadCheck = false;
    if (!bypassBlock && isBlockedUploadContainerFile(s)) {
      this.failQueuedUpload();
      return this.setState({
        error: 'File type not allowed',
        errorDescription: 'WebM and Ogg (OGV) uploads are disabled. Choose MP4, MOV, or M4V, or use Record video.',
        overlayText: this.UploadText,
        imagePreview: null,
        showOverlay: false,
      });
    }
    if (!UserAccount.isDevAccount && s.size > maxUploadBytesForFile(s)) {
      this.failQueuedUpload();
      return this.setState({
        error: 'Upload Failed',
        errorDescription: uploadTooLargeErrorDescription(s),
        overlayText: this.UploadText,
        imagePreview: null,
        showOverlay: false,
      });
    }
    const uploadPayload =
      typeof s.type === 'string' && /^video\//i.test(s.type)
        ? new File([s], videoUploadFilenameForTranscoder(s.name, {
          forcePortrait: bypassBlock && this.isForcePortraitCapture(),
        }), {
            type: s.type,
            lastModified: typeof s.lastModified === 'number' ? s.lastModified : Date.now(),
          })
        : s;
    // Converted hashing to use binary chunks instead of one large string
    let blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice,
      file = uploadPayload,
      chunkSize = 2097152,
      chunks = Math.ceil(file.size / chunkSize),
      currentChunk = 0,
      spark = new SparkMD5.ArrayBuffer(),
      fileReader = new FileReader();
    fileReader.onload = (e) => {
      spark.append(e.target.result);
      currentChunk++;
      if (currentChunk < chunks) {
        loadNext();
      } else {
        this.hashFilename = spark.end();
        this.uploadFile(uploadPayload);
      }
    };
    fileReader.onerror = () => {
      this.failQueuedUpload();
      this.setState({ error: 'Upload Failed', errorDescription: 'Something went wrong, please try again.', overlayText: this.UploadText, imagePreview: null, showOverlay: false });
    };
    function loadNext() {
      var start = currentChunk * chunkSize,
          end = ((start + chunkSize) >= file.size) ? file.size : start + chunkSize;
      fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
    }
    return loadNext();
  }
  uploadFile = (s) => {
    this.getSignedUrl({ name: s.name, type: s.type }, (url) => {
      if (url && url.signedUrl) {
        const request = new XMLHttpRequest();
        request.open("PUT", url.signedUrl, true);
        request.setRequestHeader('Content-Disposition', 'inline');
        request.setRequestHeader('Content-Type', s.type);
        request.upload.addEventListener("progress", (evt) => {
          if (evt.lengthComputable) {
            var percentComplete = evt.loaded / evt.total;
            this.setState({
              overlayText: `${Math.round(percentComplete*100)}%`,
              showOverlay: true,
            });
          }
        }, false);
        request.onreadystatechange = (evt) => {
          if (evt.target.readyState === 4) {
            this.onUploadFinish(url);
          }
        }
        request.send(s);
      } else {
        this.onUploadFinish(url);
      }
    });
  }
  onUploadFinish = (results) => {
    const pendingReviewMeta = this._pendingReviewMeta;
    this._pendingReviewMeta = null;
    if (!results || results.error) {
      this.failQueuedUpload();
      return this.setState({ error: 'Already exists', errorDescription: 'This asset has already been uploaded', overlayText: this.UploadText, showOverlay: false, imagePreview: null });
    }
    this.setState({
      overlayText: this.UploadText,
      showOverlay: false,
      imagePreview: null,
    });
    this.closeVideo({ suppressDismiss: true });
    let payload = results;
    if (pendingReviewMeta && this.isStoryReviewMode()) {
      payload = {
        ...results,
        storyTitle: pendingReviewMeta.title || undefined,
        storyDescription: pendingReviewMeta.description || undefined,
      };
    }
    this.props.onUploadFinish(payload);
    if (this.queuedResolve) {
      this.queuedResolve();
      this.queuedResolve = null;
      this.queuedFile = null;
      this.queuedNext = null;
      this.queuedUser = null;
    }
  }
  clearQueuedFile = () => {
    const { onUploadQueued } = this.props;
    CreateProject.unqueueMediaUploadTile(this);
    if (onUploadQueued) onUploadQueued(false);
    this.queuedResolve = null;
    this.queuedFile = null;
    this.queuedNext = null;
    this.queuedUser = null;
    this.setState({
      overlayText: this.UploadText,
      showOverlay: false,
      imagePreview: null
    });
  }
  clearError = () => {
    const { onFullscreenCameraDismiss } = this.props;
    if (onFullscreenCameraDismiss) {
      onFullscreenCameraDismiss();
      return;
    }
    this.setState({ error: null, errorDescription: null });
  }
  renderFullscreenCameraPortal = () => {
    if (!this.state.showVideoCamera) return null;
    const {
      cameraState, cameraPurpose, recordingElapsedMs, reviewTitle, reviewDescription, cameraFacingMode,
    } = this.state;
    const selfieMirrorStyle = isSelfieFacingMode(cameraFacingMode) ? { transform: 'scaleX(-1)' } : undefined;
    const { onlyAllowImage, onlyAllowVideo, reviewBeforeUpload, reviewRecordedVideo = true } = this.props;
    const forcePortrait = this.isForcePortraitCapture();
    const nativePortraitStory = isNativePortraitStoryCapture(forcePortrait);
    const storyReview = this.isStoryReviewMode();
    if (cameraState === 'review') {
      const reviewSrc = this._reviewObjectUrl;
      const ReviewVideo = nativePortraitStory && storyReview
        ? NativePortraitStoryReviewVideo
        : ReviewPreviewVideo;
      const reviewContent = (
        <>
          {reviewSrc && (
            <ReviewVideo src={reviewSrc} controls playsInline autoPlay loop />
          )}
          <ReviewCloseButton onClick={this.discardReview} role="presentation">
            <CloseIcon style={{ width: 20, height: 20, filter: 'invert(1)', margin: '12px 8px' }} />
          </ReviewCloseButton>
          <ReviewForm>
            <ReviewHeading>{storyReview ? 'Review your story' : 'Review your video'}</ReviewHeading>
            {storyReview && (
              <ReviewField
                type="text"
                placeholder="Title (optional)"
                value={reviewTitle}
                onChange={this.onReviewTitleChange}
                maxLength={120}
              />
            )}
            <ReviewTextarea
              placeholder="Description (optional)"
              value={reviewDescription}
              onChange={this.onReviewDescriptionChange}
              maxLength={500}
            />
            <ReviewActions>
              <ReviewActionBtn type="button" onClick={this.retakeReview}>Retake</ReviewActionBtn>
              <ReviewActionBtn type="button" onClick={this.discardReview}>Discard</ReviewActionBtn>
              <ReviewActionBtn type="button" $primary onClick={this.approveReview}>
                {storyReview ? 'Post story' : 'Upload'}
              </ReviewActionBtn>
            </ReviewActions>
          </ReviewForm>
        </>
      );
      const reviewPortal = (
        <FullscreenCameraRoot>
          {forcePortrait && !nativePortraitStory ? (
            <StoryReviewShell>
              <StoryReviewFrame>{reviewContent}</StoryReviewFrame>
            </StoryReviewShell>
          ) : reviewContent}
        </FullscreenCameraRoot>
      );
      return ReactDOM.createPortal(reviewPortal, document.body);
    }
    const isPhoto = cameraPurpose === 'photo';
    const showPurposeToggle = !onlyAllowImage && !onlyAllowVideo && !prefersCaptureAttributeForPhoto();
    const instructionTop = showPurposeToggle ? 'max(25vh, 7.85rem)' : 'max(14vh, 4rem)';
    const cameraVideoProps = {
      ref: (el) => { this.videoCanvas = el; },
      autoPlay: true,
      muted: true,
      playsInline: true,
      style: selfieMirrorStyle,
    };
    const cameraVideo = nativePortraitStory
      ? <NativePortraitStoryVideo {...cameraVideoProps} />
      : forcePortrait
        ? <PortraitCameraVideo {...cameraVideoProps} />
        : <video {...cameraVideoProps} style={{ width: '100%', height: '100%', objectFit: 'cover', ...selfieMirrorStyle }} />;
    const storyMaxMs = this.storyMaxRecordingMs();
    const recordHint = forcePortrait && !isPhoto
      ? 'Stories are up to 60 seconds. Hold your phone upright or frame yourself in the vertical guide, then tap the red button to record.'
      : (reviewBeforeUpload || reviewRecordedVideo)
        ? 'Tap the red button to record. Tap again to review before uploading.'
        : 'Tap the red button to record. Tap again to stop and upload.';
    const recordingElapsedLabel = storyMaxMs != null && cameraPurpose === 'record' && cameraState === 'recording'
      ? `REC ${formatRecordingElapsed(recordingElapsedMs || 0)} / ${formatRecordingElapsed(storyMaxMs)}`
      : `REC ${formatRecordingElapsed(recordingElapsedMs || 0)}`;
    const portal = (
      <FullscreenCameraRoot>
        {forcePortrait && !nativePortraitStory ? (
          <PortraitCameraViewport>{cameraVideo}</PortraitCameraViewport>
        ) : (
          cameraVideo
        )}
        <FlipCameraButton
          type="button"
          aria-label="Switch camera"
          disabled={cameraState === 'recording'}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            this.flipCameraFacing();
          }}
        >
          Flip
        </FlipCameraButton>
        <CloseButton onClick={this.closeVideo} role="presentation">
          <CloseIcon style={{ width: 20, height: 20, filter: 'invert(1)', margin: '12px 8px' }} />
        </CloseButton>
        {showPurposeToggle && (
          <CameraPurposeToggleWrap>
            <CameraPurposeToggleBtn
              type="button"
              aria-pressed={isPhoto ? 'true' : 'false'}
              $active={!!isPhoto}
              disabled={cameraState === 'recording'}
              onClick={() => this.switchCameraPurpose('photo')}
            >
              Photo
            </CameraPurposeToggleBtn>
            <CameraPurposeToggleBtn
              type="button"
              aria-pressed={!isPhoto ? 'true' : 'false'}
              $active={!isPhoto}
              disabled={cameraState === 'recording'}
              onClick={() => this.switchCameraPurpose('record')}
            >
              Video
            </CameraPurposeToggleBtn>
          </CameraPurposeToggleWrap>
        )}
        {cameraState !== 'recording' && !(forcePortrait && cameraPurpose === 'record') && (
          <RecordingText style={{ position: 'absolute', top: instructionTop, left: '1rem', right: '1rem', textAlign: 'center', fontSize: Constants.SemiSmallFontSize, opacity: 0.92, pointerEvents: 'none', lineHeight: 1.45 }}>
            {isPhoto ? 'Frame your shot, then tap the red button to capture.' : recordHint}
          </RecordingText>
        )}
        {cameraPurpose === 'record' && cameraState === 'recording' && (
          <RecordingElapsed>
            {recordingElapsedLabel}
          </RecordingElapsed>
        )}
        <RecordButton>
          <button
            type="button"
            aria-label={isPhoto ? 'Capture photo' : 'Start or stop recording'}
            onClick={isPhoto ? this.captureStillPhotoFromPreview : this.toggleRecording}
          />
        </RecordButton>
      </FullscreenCameraRoot>
    );
    return ReactDOM.createPortal(portal, document.body);
  }
  getContentType = () => {
    const { fileType, backgroundImage, onlyAllowImage, onlyAllowVideo } = this.props;
    try {
      if (!backgroundImage) return {};

      // Check the url path
      const url = new URL(backgroundImage);
      const { pathname } = url;
      const name = pathname.split('/').slice(-1) || '';
      let isApp = fileType === 'folder';
      let isPDF = ['.pdf'].find((ext) => pathname.toLowerCase().endsWith(ext));
      let isHTML = ['.html'].find((ext) => pathname.toLowerCase().endsWith(ext));
      let isImage = fileType?.includes('image/') || IMAGE_EXTENSIONS.find((ext) => pathname.toLowerCase().endsWith(ext));
      let isVideo = fileType?.includes('video/')
        || fileType === 'application/x-mpegURL'
        || VIDEO_EXTENSIONS.find((ext) => pathname.toLowerCase().endsWith(`.${ext}`));

      // If only images allowed, return immediately
      if (onlyAllowImage) return { name, isImage: true };
      if (onlyAllowVideo) return { name, isVideo: true };

      // ONLY CALL HEAD if filetype not found
      let { contentType } = this.state;
      if (contentType) {
        isPDF = contentType?.startsWith('application/pdf');
        isHTML = contentType?.startsWith('text/html');
        isImage = contentType?.startsWith('image/');
        isVideo = contentType?.startsWith('video/') || contentType === 'application/x-mpegURL';
      } else if (!(isApp || isPDF || isHTML || isImage || isVideo)) {
        if (!this.gettingContentType) {
          this.gettingContentType = true;
          Server.getContentType(backgroundImage).then((contentType) => {
            this.gettingContentType = false;
            this.setState({ contentType });
          });
        }
      }
      return { name, isApp, isPDF, isHTML, isImage, isVideo, contentType };

    } catch {
      return {};
    }
  }
  render() {
    const {
      disableEditing, allowDeleting, onDelete, backgroundColor, disableUpload, fileType, onlyAllowImage, onlyAllowVideo, hideUploadText, suppressReadonlyAssetPreview,
      uploadChoiceMenu, uploadIcon, onRequestFolderUpload, folderPickOnly, suppressTapTarget, createdTime,
    } = this.props;
    let { backgroundImage } = this.props;
    const { imagePreview, showVideoCamera, showAssetPopup, nodeRef, showOverlay, showUploadMenu } = this.state;
    const style = this.props.style || {};
    const small = style.height && style.height < 100;

    let showBackgroundText;
    let assetUrl;
    let { name, isApp, isPDF, isHTML, isImage, isVideo, isIframeable } = this.getContentType();
    isIframeable = isApp || isPDF || isHTML;
    showBackgroundText = !onlyAllowImage && !onlyAllowVideo && !isImage && !isVideo;
    assetUrl = (isApp ? `${backgroundImage}/index.html` : backgroundImage);
    /** Grid asset cards: don't paint folder uploads neon green or stack the hash on the thumb. */
    const readonlyFolderCard = disableEditing && isApp && !small;
    if (name) {
      if (isApp) {
        const date = (new Date(parseInt(name)));
        if (!isNaN(name)) name = date.toLocaleString().replace(',', '\r\n');
      }
      else name = `${name}${fileType ? ` (${fileType})` : ''}`;
    }
    const openAsset = () => {
      if (suppressReadonlyAssetPreview) return;
      if (!(assetUrl && disableEditing)) return;
      return this.setState({ showAssetPopup: true });
    };
    const width = style.width === style.height ? 480 : 720;
    const height = style.width === style.height ? 480 : 405;
    const assetContent = assetUrl ? checkContentType(assetUrl) : {};
    const videoAssetUrl = isVideo && logictryUploadedVideoToCdnPlaybackUrl(assetUrl);
    const useLogictryIframeVideoPlayer =
      isVideo &&
      assetUrl &&
      assetContent.isUserUploadVideoPlaybackUrl;
    const videoStillProcessing = isVideo
      && uploadedVideoNeedsTranscode(assetUrl)
      && videoTranscodeProcessingRemaining(createdTime) > 0;
    const showUnifiedCamera = !onlyAllowImage && !onlyAllowVideo && !prefersCaptureAttributeForPhoto();
    const touchMediaUx = prefersCaptureAttributeForPhoto();
    const filePickAccept = onlyAllowImage ? 'image/*' : onlyAllowVideo ? 'video/*' : '*';
    const nonImageThumbColor = disableEditing && showBackgroundText
      ? (backgroundColor || (isApp ? Constants.StaticGray : Constants.PrimaryColor))
      : null;
    const showFilenameOverlay = !(disableEditing && isApp) && (
      small
      || isHTML
      || (showBackgroundText && !isIframeable)
      || (isApp && !disableEditing)
    );
    /** Folder uploads used to hit the filename branch (isApp); without that, avoid embedding index.html in every grid thumb. */
    const showIframePreview = showBackgroundText && !(disableEditing && isApp);
    return (
      <>
      <HoverDiv style={{ pointerEvents: (showOverlay && !this.queuedFile) ? 'none': null, position: 'relative', backgroundColor: nonImageThumbColor, borderRadius: style.borderRadius }}>
        {showFilenameOverlay && (
          <FileName style={{ justifyContent: small ? null : 'center', color: small ? Constants.DarkTextColor : null, left: small ? 40 : 0, right: small ? 70 : 0, whiteSpace: small ? 'nowrap' : null }}>
            <div style={{ whiteSpace: (isApp && !small) ? 'break-spaces' : null }}>{name}</div>
          </FileName>
        ) || (showIframePreview && <div onClick={openAsset}><Iframe><iframe src={assetUrl}></iframe></Iframe></div>)}
        <div onClick={openAsset}>
          <ProfilePic style={{
            display: 'block',
            backgroundSize: style.backgroundSize || 'contain',
            backgroundPosition: style.backgroundPosition,
            backgroundRepeat: style.backgroundRepeat,
            border: style.border,
            borderRadius: style.borderRadius,
            width: style.width || style.height,
            height: style.height,
            aspectRatio: style.aspectRatio,
            maxHeight: style.maxHeight,
            minHeight: style.minHeight,
            lineHeight: style.height != null ? `${style.height}px` : undefined,
            position: 'relative',
            backgroundImage: ((imagePreview && `url(${imagePreview})`) || isImage && `${assetUrl && !showBackgroundText ? `url(${convertUrlfromS3toCloudFront(assetUrl, width, height)})` : ''}`) }}>
            {assetUrl && isVideo && (
              <div style={{ pointerEvents: 'none', position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 1 }}>
                {videoStillProcessing
                  ? <VideoProcessingLabel />
                  : useLogictryIframeVideoPlayer
                  ? (
                    <iframe
                      title=""
                      aria-hidden="true"
                      src={assetContent.logictryUserUploadVideoIframeUrl}
                      loading="lazy"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        border: 0,
                        pointerEvents: 'none',
                      }}
                    />
                  )
                  : (
                    <video
                      playsInline
                      preload="metadata"
                      width="100%"
                      height="100%"
                    >
                      <source
                        src={`${videoAssetUrl}`}
                        type={(/\.m3u8(\?|#|$)/i.test(videoAssetUrl) ? 'application/x-mpegURL' : undefined)}
                      />
                    </video>
                  )}
              </div>
            )}
            {readonlyFolderCard && (
              <div style={{
                pointerEvents: 'none',
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                opacity: 1,
              }}
              >
                <HtmlIcon style={{ width: 40, height: 40, opacity: 0.2 }} />
              </div>
            )}
            {!disableEditing && !showVideoCamera && !suppressTapTarget && (
              <div style={{ position: 'absolute', inset: 0 }}>
                {uploadChoiceMenu && !NativeCamera.avoidWebKitFileInputFallback() && (
                  <>
                    <input
                      ref={(e) => { this._filePickInput = e; }}
                      type="file"
                      accept={filePickAccept}
                      onChange={this.uploadExistingFileAndReset}
                      style={{ display: 'none' }}
                      aria-hidden
                    />
                    {!onlyAllowVideo && (
                      <>
                        <input
                          ref={(e) => { this._capturePhotoInput = e; }}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={this.uploadExistingFileAndReset}
                          style={{ display: 'none' }}
                          aria-hidden
                        />
                        <input
                          ref={(e) => { this._photoLibraryInput = e; }}
                          type="file"
                          accept="image/*"
                          onChange={this.uploadExistingFileAndReset}
                          style={{ display: 'none' }}
                          aria-hidden
                        />
                      </>
                    )}
                  </>
                )}
                {uploadChoiceMenu ? (
                  <button
                    type="button"
                    aria-label={this.UploadText}
                    onClick={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      if (disableUpload) this.showUploadDisabledPopup();
                      else this.setState({ showUploadMenu: true });
                    }}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 1 }}
                  />
                ) : folderPickOnly ? (
                  <button
                    type="button"
                    aria-label={this.UploadText}
                    onClick={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      if (disableUpload) this.showUploadDisabledPopup();
                      else if (onRequestFolderUpload) onRequestFolderUpload();
                    }}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 1 }}
                  />
                ) : touchMediaUx ? (
                  <>
                    {/*
                      Non-menu tap targets on phones (profile avatar `onlyAllowImage`, Content Source `uploadPickerMode="file"`, etc.):
                      Capacitor gallery when registered; otherwise hidden `image/*` input (web only).
                    */}
                    {!NativeCamera.avoidWebKitFileInputFallback() && (
                      <input
                        ref={(e) => { this._touchPhotoLibraryFallbackInput = e; }}
                        type="file"
                        accept="image/*"
                        onChange={this.uploadExistingFileAndReset}
                        style={{ display: 'none' }}
                        aria-hidden
                      />
                    )}
                    <button
                      type="button"
                      aria-label={this.UploadText}
                      onClick={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        if (disableUpload) this.showUploadDisabledPopup();
                        else this.triggerPhotoLibraryPick();
                      }}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 1 }}
                    />
                  </>
                ) : (
                  <label style= {{ position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: 'pointer', zIndex: 1 }}>
                    <input
                      type="file"
                      accept={filePickAccept}
                      onChange={this.uploadExistingFile}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, pointerEvents: 'none' }}
                    />
                  </label>
                )}
              </div>
            )}
            {disableEditing && small && showBackgroundText && (
              <div style={{ pointerEvents: 'none', opacity: 1, lineHeight: `${style.height}px`, cursor: 'pointer', fontSize: Constants.SmallFontSize, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ImagesIcon style={{ width: 16, height: 16, filter: 'invert(1)' }} />
              </div>
            )}
            {!disableEditing && (small ? (
              <div style={{ pointerEvents: 'none', opacity: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                {uploadIcon || <UploadIcon style={{ width: 16, height: 16 }} />}
                {showOverlay && <UploadOverlay style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, color: (assetUrl || showOverlay) ? 'white' : null, backgroundColor: (assetUrl || showOverlay) ? 'rgba(0,0,0,0.7)' : null, pointerEvents: 'none', opacity: `${!assetUrl || showOverlay ? '1' : '0'}`, lineHeight: `${style.height}px` }}>{this.state.overlayText}</UploadOverlay>}
              </div>
            ) : (
              <UploadOverlay style={{ padding: '0.5rem', color: (assetUrl || showOverlay) ? 'white' : null, backgroundColor: (assetUrl || showOverlay) ? 'rgba(0,0,0,0.7)' : null, pointerEvents: 'none', opacity: `${!assetUrl || showOverlay ? '1' : '0'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div>{this.state.overlayText}</div>
              </UploadOverlay>
            ))}
          </ProfilePic>
        </div>
        <ConfirmDialog
          open={!!(!disableEditing && this.state.error)}
          text={this.state.error || ''}
          description={this.state.errorDescription}
          onOk={this.clearError}
        />
        {showUploadMenu && !disableEditing && (
          <Popup showCloseButton allowBackgroundClose onClose={this.closeUploadMenu}>
            <UploadChoicePanel>
              <ModalSheetTitle style={{ textAlign: 'center', margin: '0 0 0.25rem', fontWeight: 700 }}>
                Add media
              </ModalSheetTitle>
              <SmallHint style={{ marginTop: 0, marginBottom: '1.05rem', textAlign: 'center' }}>
                {onlyAllowVideo
                  ? 'Record a video with the camera or choose a video file.'
                  : showUnifiedCamera
                  ? (
                    <>
                      Use camera for photo or video, or upload files
                      {typeof onRequestFolderUpload === 'function' ? ' (developer folder uploads available below).' : '.'}
                    </>
                  )
                  : onlyAllowImage
                    ? (touchMediaUx ? 'Take a photo with your camera or pick one from your photo library.' : 'Capture a photo or choose an image file.')
                    : touchMediaUx
                      ? (
                        <>
                          Record video or take a photo with the camera, or pick saved photos from your library. To browse all file types, use a desktop browser.
                          {typeof onRequestFolderUpload === 'function' ? ' Developer folder upload is available below on supported devices.' : ''}
                        </>
                      )
                      : (
                        <>
                          Record video, capture a photo, or upload files
                          {typeof onRequestFolderUpload === 'function' ? ' (developer folder upload below).' : '.'}
                        </>
                      )}
              </SmallHint>
              {showUnifiedCamera && (
                <UploadMenuButton type="button" onClick={this.menuUseCamera}>Use camera</UploadMenuButton>
              )}
              {!onlyAllowImage && !showUnifiedCamera && (
                <UploadMenuButton type="button" onClick={this.menuRecordVideo}>Record video</UploadMenuButton>
              )}
              {!showUnifiedCamera && !onlyAllowVideo && (
                <UploadMenuButton type="button" onClick={this.menuTakePhoto}>Take photo</UploadMenuButton>
              )}
              {touchMediaUx && !onlyAllowVideo && (
                <UploadMenuButton type="button" onClick={this.menuChoosePhotoLibrary}>Photo library</UploadMenuButton>
              )}
              {(!touchMediaUx || onlyAllowVideo) && (
                <UploadMenuButton type="button" onClick={this.menuChooseFile}>Choose file</UploadMenuButton>
              )}
              {typeof onRequestFolderUpload === 'function' && !onlyAllowImage && !onlyAllowVideo && (
                <UploadMenuButton type="button" onClick={this.menuUploadFolder}>Upload folder</UploadMenuButton>
              )}
            </UploadChoicePanel>
          </Popup>
        )}
        {allowDeleting && <DeleteButton>
          <DeleteIcon
            style={{ width: 16, height: 16, filter: 'invert(1)', margin: 16 }}
            onClick={onDelete} />
        </DeleteButton>}
        {!hideUploadText && !disableEditing && small && <UploadOverlay style={{ marginLeft: '0.5rem' }}>{this.UploadText}</UploadOverlay>}
        {disableUpload && <DisableUploadOverlay onClick={this.showUploadDisabledPopup} />}
        {this.queuedFile && <DeleteButton>
          <DeleteIcon
            style={{ width: 16, height: 16, filter: 'invert(1)', margin: 16 }}
            onClick={this.clearQueuedFile} />
        </DeleteButton>}
        {showAssetPopup && <Popup
          showCloseButton
          onClose={() => this.setState({ showAssetPopup: false })}
          allowBackgroundClose
        >
          <PopupWrapper>
            {isImage && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <img style={{ maxWidth: '100%', maxHeight: '100%' }} src={convertUrlfromS3toCloudFront(assetUrl)}></img>
            </div>
              || isVideo && (
                videoStillProcessing
                  ? <VideoProcessingLabel />
                  : useLogictryIframeVideoPlayer
                  ? (
                    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 0 }}>
                      <iframe
                        title="Video preview"
                        src={assetContent.logictryUserUploadVideoIframeUrl}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          border: 0,
                        }}
                        allow="autoplay"
                      />
                    </div>
                  )
                  : <video playsInline controls src={videoAssetUrl}></video>
              )
              || isIframeable && <iframe src={assetUrl}></iframe>
              || <h1 style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white' }}>Preview Unavailable</h1>}
            <div style={{ position: 'fixed', top: '-100vh' }} key={nodeRef} ref={(e) => { this.setState({ nodeRef: e }); }}>{videoAssetUrl || assetUrl}</div>
            <div>
              <CopyHtmlToClipboardButton target={nodeRef} text="Copy Link" copiedText="Copied!" />
            </div>
          </PopupWrapper>
        </Popup>}
      </HoverDiv>
      {this.renderFullscreenCameraPortal()}
      </>
    );
  }
}
